import { useMemo } from 'react'

import { micrositesMetadataList } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/constants'
import { CMSMicrositeMetadata } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/types'
import { LDFlags, useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'

/**
 * Microsite feature flags are not declared on LDFlags, so they are looked up by name.
 * A microsite without a feature flag is always enabled.
 */
export const isMicrositeFlagEnabled = (flags: LDFlags, featureFlag: string | undefined): boolean => {
  if (featureFlag === undefined) {
    return true
  }
  // Index `flags` directly, never spread it. Outside production it is a Proxy that only traps `get`, so a spread
  // would drop flags that exist only as `?flags=`/localStorage overrides.
  const flagsByName = flags as unknown as Partial<Record<string, unknown>>
  return Boolean(flagsByName[featureFlag])
}

/**
 * Filters the list of all cms microsites to only include those that are enabled based on feature flag.
 *
 * This allows support for showing different versions of a microsite at the same url (`micrositeUrl`). Eg at the end of 2025, we need to create the 2026 new year toolkit. We can add the 2026 toolkit (shown at the same url as 2025 /members) and disable 2025 on staging and enable 2026 for testing, while still showing 2025 on production.
 */
export const useActiveMicrositesMetadataList = (): CMSMicrositeMetadata[] => {
  const flags = useFlags()

  return useMemo(() => {
    const activeMicrosites: CMSMicrositeMetadata[] = []

    for (const metadata of micrositesMetadataList) {
      if (isMicrositeFlagEnabled(flags, metadata.featureFlag)) {
        activeMicrosites.push(metadata)
      }
    }
    return activeMicrosites
  }, [flags])
}
